import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useIntl } from '~/techstyle-shared/react-intl';

import { birthdayYearList, birthdayDaysList } from '../../utils/dates';
import StyledSelect from '../StyledSelect';

const BirthdayInputGroup = styled.div`
  display: flex;

  fieldset {
    flex: 1;
  }
`;

const MonthSelect = styled(StyledSelect)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;
const DaySelect = styled(StyledSelect)`
  ${({ $showBirthYear }) =>
    $showBirthYear
      ? css`
          border-radius: 0;
          // transparent is used to prevent 1px shift on focus (which has a border)
          border-left: 1px solid transparent;
          border-right: 1px solid transparent;
        `
      : css`
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        `}
`;

const YearSelect = styled(StyledSelect)`
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

const BirthdayPicker = ({
  birthDay,
  birthMonth,
  birthYear = '',
  className,
  errors,
  handleChange,
  showBirthYear = true,
  showBirthDay = true,
}) => {
  const intl = useIntl();

  return (
    <BirthdayInputGroup className={className}>
      <MonthSelect
        onChange={handleChange}
        name="birthMonth"
        id="birthMonth"
        value={birthMonth}
        label={intl.formatMessage({
          id: 'site_account_profile.birthMonth',
          defaultMessage: 'MM',
        })}
        data-autotag="account_information_birthday_month_dropdown"
        data-pii="pii-birth-month"
        error={errors?.birthMonth}
      >
        <option value={undefined} hidden />
        <option value="01">
          {intl.formatMessage({
            id: 'site_account_profile.january',
            defaultMessage: 'January',
          })}
        </option>
        <option value="02">
          {intl.formatMessage({
            id: 'site_account_profile.february',
            defaultMessage: 'February',
          })}
        </option>
        <option value="03">
          {intl.formatMessage({
            id: 'site_account_profile.march',
            defaultMessage: 'March',
          })}
        </option>
        <option value="04">
          {intl.formatMessage({
            id: 'site_account_profile.april',
            defaultMessage: 'April',
          })}
        </option>
        <option value="05">
          {intl.formatMessage({
            id: 'site_account_profile.may',
            defaultMessage: 'May',
          })}
        </option>
        <option value="06">
          {intl.formatMessage({
            id: 'site_account_profile.june',
            defaultMessage: 'June',
          })}
        </option>
        <option value="07">
          {intl.formatMessage({
            id: 'site_account_profile.july',
            defaultMessage: 'July',
          })}
        </option>
        <option value="08">
          {intl.formatMessage({
            id: 'site_account_profile.august',
            defaultMessage: 'August',
          })}
        </option>
        <option value="09">
          {intl.formatMessage({
            id: 'site_account_profile.september',
            defaultMessage: 'September',
          })}
        </option>
        <option value="10">
          {intl.formatMessage({
            id: 'site_account_profile.october',
            defaultMessage: 'October',
          })}
        </option>
        <option value="11">
          {intl.formatMessage({
            id: 'site_account_profile.november',
            defaultMessage: 'November',
          })}
        </option>
        <option value="12">
          {intl.formatMessage({
            id: 'site_account_profile.december',
            defaultMessage: 'December',
          })}
        </option>
      </MonthSelect>
      {showBirthDay && (
        <DaySelect
          onChange={handleChange}
          name="birthDay"
          id="birthDay"
          value={birthDay}
          label={intl.formatMessage({
            id: 'site_account_profile.birthDay',
            defaultMessage: 'DD',
          })}
          data-autotag="account_information_birthday_day_dropdown"
          data-pii="pii-birth-day"
          $showBirthYear={showBirthYear}
          error={errors?.birthDay}
        >
          <option value={undefined} hidden />
          {birthdayDaysList(birthYear, birthMonth).map((day) => (
            <option
              key={day}
              value={parseInt(day)}
              data-testid="birth-day-option"
            >
              {day}
            </option>
          ))}
        </DaySelect>
      )}
      {showBirthYear && (
        <YearSelect
          onChange={handleChange}
          name="birthYear"
          id="birthYear"
          value={birthYear}
          label={intl.formatMessage({
            id: 'site_account_profile.birthYear',
            defaultMessage: 'YYYY',
          })}
          data-autotag="account_information_birthday_year_dropdown"
          data-pii="pii-birth-year"
          error={errors?.birthYear}
        >
          <option value={undefined} hidden />
          {birthdayYearList().map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </YearSelect>
      )}
    </BirthdayInputGroup>
  );
};

BirthdayPicker.propTypes = {
  birthDay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  birthMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  birthYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  errors: PropTypes.any,
  handleChange: PropTypes.func,
  showBirthDay: PropTypes.bool,
  showBirthYear: PropTypes.bool,
};

export default BirthdayPicker;
